/**
 * PwaManager.ts
 * Manages Service Worker registration, lifecycle updates, and online/offline status notifications.
 */

import { renderUiGouacheIcon } from '../ui/icons/IconRenderer';

export interface PwaStatusListener {
  onOnlineChange?: (isOnline: boolean) => void;
  onUpdateAvailable?: (reloadCallback: () => void) => void;
}

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export class PwaManager {
  private static instance: PwaManager | null = null;
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private registration: ServiceWorkerRegistration | null = null;
  private listeners: PwaStatusListener[] = [];
  private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
  private static readonly BANNER_DISMISSED_KEY = 'pocket_life_ios_a2hs_dismissed';

  private constructor() {
    this.setupConnectivityListeners();
    this.setupInstallPromptListeners();
  }

  public static getInstance(): PwaManager {
    if (!PwaManager.instance) {
      PwaManager.instance = new PwaManager();
    }
    return PwaManager.instance;
  }

  /**
   * Returns true when the game is running as an installed Home Screen Web App
   * (iOS Safari navigator.standalone or W3C display-mode: standalone).
   */
  public isStandaloneApp(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const mediaStandalone =
      typeof window.matchMedia === 'function' &&
      (window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches);
    return iosStandalone || mediaStandalone;
  }

  /**
   * Detects iOS / iPadOS Safari environment where "Add to Home Screen" is performed via the Safari Share sheet.
   */
  public isIosSafari(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isWebKit = /WebKit/i.test(ua);
    const isThirdPartyIosBrowser = /CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
    return isIOSDevice && isWebKit && !isThirdPartyIosBrowser;
  }

  public canPromptNativeInstall(): boolean {
    return this.deferredInstallPrompt !== null && !this.isStandaloneApp();
  }

  public async promptNativeInstall(): Promise<boolean> {
    if (!this.deferredInstallPrompt) return false;
    try {
      await this.deferredInstallPrompt.prompt();
      const choice = await this.deferredInstallPrompt.userChoice;
      this.deferredInstallPrompt = null;
      return choice.outcome === 'accepted';
    } catch {
      return false;
    }
  }

  public isInstallBannerDismissed(): boolean {
    try {
      if (typeof sessionStorage !== 'undefined') {
        return sessionStorage.getItem(PwaManager.BANNER_DISMISSED_KEY) === '1';
      }
    } catch {
      // Ignore restricted storage errors
    }
    return false;
  }

  public dismissInstallBanner(): void {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(PwaManager.BANNER_DISMISSED_KEY, '1');
      }
    } catch {
      // Ignore storage errors
    }
    if (typeof document !== 'undefined') {
      document.getElementById('safari-a2hs-banner')?.remove();
    }
  }

  /**
   * Mounts a compact, dismissible iOS Safari "Add to Home Screen" banner when opened in mobile Safari browser mode.
   */
  public mountSafariInstallBanner(onOpenGuide?: () => void): HTMLElement | null {
    if (typeof document === 'undefined') return null;
    if (this.isStandaloneApp() || this.isInstallBannerDismissed()) return null;

    // Only auto-mount on iOS Safari or when URL explicitly requests ?install=1
    const forceShow =
      typeof window !== 'undefined' &&
      window.location &&
      new URLSearchParams(window.location.search).get('install') === '1';

    if (!this.isIosSafari() && !forceShow) return null;

    const existing = document.getElementById('safari-a2hs-banner');
    if (existing) return existing;

    const shell = document.getElementById('app-shell') || document.body;
    const banner = document.createElement('div');
    banner.id = 'safari-a2hs-banner';
    banner.setAttribute('role', 'region');
    banner.setAttribute('aria-label', 'Hướng dẫn thêm ứng dụng vào màn hình chính Safari');
    banner.style.cssText = `
      position: absolute;
      left: 10px;
      right: 10px;
      bottom: calc(var(--safe-bottom, 34px) + 72px);
      z-index: 35;
      background: linear-gradient(180deg, #FFFDF7 0%, #F5E6C8 100%);
      border: 2.2px solid #3B2314;
      border-radius: 15px;
      padding: 10px 12px;
      box-shadow: 0 4px 0 #3B2314, inset 0 1.5px 0 #FFFFFF, 0 12px 24px rgba(34, 19, 10, 0.42);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    `;

    banner.innerHTML = `
      <div style="min-width: 0; flex: 1; font-size: 11px; line-height: 1.38; color: #3B2314;">
        <div style="font-weight: 900; color: #3B2314; margin-bottom: 2px; display: flex; align-items: center; gap: 5px;">
          ${renderUiGouacheIcon('icon_home_cottage', { size: 17 })}
          <span>Chuyển thành App trên iPhone (Safari)</span>
        </div>
        <div style="color: #5C3D2E; font-size: 10px; font-weight: 600;">
          Nhấn nút <strong>Chia sẻ [⬆️]</strong> ➔ Chọn <strong>"Thêm vào Màn hình chính" [➕]</strong> để chơi toàn màn hình!
        </div>
      </div>
      <div style="display: flex; align-items: center; gap: 6px; flex-shrink: 0;">
        <button id="btn-a2hs-open-guide" type="button" style="
          background: linear-gradient(180deg, #FFE8A3 0%, #F4B961 100%);
          border: 1.8px solid #3B2314;
          box-shadow: 0 2px 0 #3B2314, inset 0 1px 0 rgba(255, 255, 255, 0.75);
          border-radius: 9px;
          padding: 6px 10px;
          color: #3B2314;
          font-size: 10px;
          font-weight: 900;
          cursor: pointer;
          white-space: nowrap;
        ">Hướng dẫn</button>
        <button id="btn-a2hs-dismiss" type="button" aria-label="Đóng hướng dẫn cài đặt" style="
          background: linear-gradient(180deg, #FAD4C4 0%, #E7967C 100%);
          border: 1.8px solid #3B2314;
          box-shadow: 0 2px 0 #3B2314;
          border-radius: 9px;
          width: 28px;
          height: 28px;
          color: #2D160D;
          font-size: 11px;
          font-weight: 900;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        ">✕</button>
      </div>
    `;

    shell.appendChild(banner);

    banner.querySelector('#btn-a2hs-open-guide')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      onOpenGuide?.();
    });

    banner.querySelector('#btn-a2hs-dismiss')?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.dismissInstallBanner();
    });

    return banner;
  }

  public registerServiceWorker(): void {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      console.log('[PWA] Service Worker not supported in this browser.');
      return;
    }

    const doRegister = async () => {
      try {
        const registration = await navigator.serviceWorker.register('./sw.js', {
          scope: './'
        });
        this.registration = registration;
        registration.update().catch(() => {});
        console.log('[PWA] ServiceWorker registered with scope:', registration.scope);

        // Listen for new worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[PWA] New version ready for activation.');
              this.notifyUpdateAvailable(() => {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              });
            }
          });
        });

        // Controller change reload
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('[PWA] Service Worker controller changed.');
        });
      } catch (err) {
        console.warn('[PWA] ServiceWorker registration failed:', err);
      }
    };

    if (typeof document !== 'undefined' && document.readyState === 'complete') {
      doRegister();
    } else {
      window.addEventListener('load', doRegister, { once: true });
    }
  }

  public addListener(listener: PwaStatusListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  public getOnlineStatus(): boolean {
    return this.isOnline;
  }

  public getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }

  private setupConnectivityListeners(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      this.isOnline = true;
      this.listeners.forEach((l) => l.onOnlineChange?.(true));
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.listeners.forEach((l) => l.onOnlineChange?.(false));
    });
  }

  private setupInstallPromptListeners(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredInstallPrompt = e as BeforeInstallPromptEvent;
    });

    window.addEventListener('appinstalled', () => {
      this.deferredInstallPrompt = null;
      this.dismissInstallBanner();
    });
  }

  private notifyUpdateAvailable(reloadCallback: () => void): void {
    this.listeners.forEach((l) => l.onUpdateAvailable?.(reloadCallback));
  }
}
